/* global FormData */
import { logUtil } from 'packages/src'
import fetchNetworkUtil from 'modules/training/modules/network/utils/fetch'

const TRAINING_API_ENDPOINT = process.env.TRAINING_API_ENDPOINT

/**
 * Creates an exercise form check by uploading the video and submitting the analysis request
 * @param {Object} props
 * @param {File} props.file - The video file to upload
 * @param {number} [props.exerciseId] - Optional exercise ID for reference
 * @param {string} [props.source] - Source of the form check request
 * @returns {Function} Redux thunk
 */
export const createExerciseFormCheckAction = ({
  file,
  exerciseId,
  source = 'CHECK_EXERCISE_FORM_TOOL'
}) => {
  return async (dispatch) => {
    const logPrefix = `exerciseId=${exerciseId} source=${source}`
    logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [start]`)

    try {
      // Step 1: Upload the video file to get mediaId
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 1] preparing formData`)
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', file.name)

      const mediaEndpoint = `${TRAINING_API_ENDPOINT}/media?type=2`
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 1] uploading to ${mediaEndpoint}`)

      const mediaResponse = await fetchNetworkUtil(mediaEndpoint, {
        credentials: 'include',
        method: 'POST',
        body: formData
      }, 60000)

      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 1] response status=${mediaResponse.status} ok=${mediaResponse.ok}`)

      const mediaJson = await mediaResponse.json()
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 1] response json=`, mediaJson)

      if (!mediaResponse.ok) {
        logUtil('createExerciseFormCheckAction').error(`${logPrefix} [step 1] response not ok, error=${mediaJson?.error}`)
        throw new Error(mediaJson?.error || 'Failed to upload video')
      }

      if (!mediaJson?.data?.media?.id) {
        logUtil('createExerciseFormCheckAction').error(`${logPrefix} [step 1] no mediaId in response, mediaJson=`, mediaJson)
        throw new Error(mediaJson?.error || 'Failed to upload video')
      }

      const mediaId = mediaJson.data.media.id
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 1] success, mediaId=${mediaId}`)

      // Step 2: Create the exercise form check
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 2] preparing request body`)
      const requestBody = {
        mediaId,
        source,
        platform: 'WEB',
        mediaBase64: ''
      }

      // Only include exerciseId if provided
      if (exerciseId) {
        requestBody.exerciseId = exerciseId
      }

      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 2] requestBody=`, requestBody)

      const formCheckEndpoint = `${TRAINING_API_ENDPOINT}/exercise-form-checks`
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 2] posting to ${formCheckEndpoint}`)

      const formCheckResponse = await fetchNetworkUtil(formCheckEndpoint, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify(requestBody)
      }, 60000)

      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 2] response status=${formCheckResponse.status} ok=${formCheckResponse.ok}`)

      const formCheckJson = await formCheckResponse.json()
      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [step 2] response json=`, formCheckJson)

      if (!formCheckResponse.ok) {
        logUtil('createExerciseFormCheckAction').error(`${logPrefix} [step 2] response not ok, error=${formCheckJson?.error}`)
        throw new Error(formCheckJson?.error || 'Failed to create form check')
      }

      logUtil('createExerciseFormCheckAction').debug(`${logPrefix} [success]`, formCheckJson)

      return formCheckJson
    } catch (error) {
      logUtil('createExerciseFormCheckAction').error(`${logPrefix} [error]`, error)
      throw error
    }
  }
}
